'use client'

import Image from 'next/image'
import Link from 'next/link'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { motion, useReducedMotion } from 'framer-motion'

import {
  softEditorialEase,
  softMotionDuration,
} from '@/shared/components/motion/motion-tokens'
import certificateImage from '@/images/sample-medical-certificate.png'
import {
  certificateParts,
  partKindMeta,
  type CertificatePart,
} from './certificate-parts'

const IMAGE_ASPECT = certificateImage.width / certificateImage.height
const MAX_ZOOM = 3
const MIN_ZOOM = 1.2
const REGION_PADDING = 1.08
const VIEWER_INSET = 28

type Step =
  | { type: 'overview'; id: string; label: string; title: string; body: string }
  | { type: 'part'; id: string; part: CertificatePart; index: number }

const steps: Step[] = [
  {
    type: 'overview',
    id: 'intro',
    label: 'Start at the top',
    title: 'One page, read top to bottom.',
    body: 'Employers and schools read this document the same way, so every element sits where they expect it. Keep scrolling — we’ll walk it line by line.',
  },
  ...certificateParts.map(
    (part, index): Step => ({
      type: 'part',
      id: part.id,
      part,
      index,
    }),
  ),
  {
    type: 'overview',
    id: 'outro',
    label: 'The whole picture',
    title: 'Eight parts, zero filler.',
    body: 'Nothing on the page is decoration. Download the sample — the watermark, the people and the reference are the only things that aren’t real.',
  },
]

export function CertificateScrolly() {
  const prefersReducedMotion = useReducedMotion()
  const [activeIndex, setActiveIndex] = useState(0)
  const stepRefs = useRef<Array<HTMLDivElement | null>>([])
  const windowRef = useRef<HTMLDivElement | null>(null)
  const [windowSize, setWindowSize] = useState<{ w: number; h: number } | null>(
    null,
  )

  useEffect(() => {
    const node = windowRef.current
    if (!node) return

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      if (width > 0 && height > 0) setWindowSize({ w: width, h: height })
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const nodes = stepRefs.current.filter(Boolean) as HTMLDivElement[]
    if (nodes.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const index = nodes.indexOf(entry.target as HTMLDivElement)
          if (index !== -1) setActiveIndex(index)
        }
      },
      { rootMargin: '-45% 0px -45% 0px' },
    )
    nodes.forEach((node) => observer.observe(node))
    return () => observer.disconnect()
  }, [])

  const activeStep = steps[activeIndex]
  const activePart = activeStep.type === 'part' ? activeStep.part : null

  // Camera: pan/zoom the certificate inside the stage window so the active
  // part fills the frame. All values in px, derived from the measured window.
  const camera = useMemo(() => {
    if (!windowSize) return { imgW: 0, imgH: 0, x: 0, y: 0, scale: 1 }

    const { w: W, h: H } = windowSize
    const imgW = Math.min(W - VIEWER_INSET, (H - VIEWER_INSET) * IMAGE_ASPECT)
    const imgH = imgW / IMAGE_ASPECT

    if (!activePart) {
      return { imgW, imgH, x: -imgW / 2, y: -imgH / 2, scale: 1 }
    }

    const { x, y, w, h } = activePart.region
    const scale = Math.min(
      MAX_ZOOM,
      Math.max(
        MIN_ZOOM,
        Math.min(
          W / (w * imgW * REGION_PADDING),
          H / (h * imgH * REGION_PADDING),
        ),
      ),
    )
    const cx = x + w / 2
    const cy = y + h / 2

    // Centre the region in the window, but never pan the paper's edge inside
    // the frame when the scaled paper is big enough to cover it.
    const clampAxis = (desired: number, scaledSize: number, windowSize_: number) => {
      if (scaledSize <= windowSize_) return 0
      const limit = (scaledSize - windowSize_) / 2
      return Math.min(limit, Math.max(-limit, desired))
    }
    const tx = clampAxis(-scale * (cx - 0.5) * imgW, imgW * scale, W)
    const ty = clampAxis(-scale * (cy - 0.5) * imgH, imgH * scale, H)

    return {
      imgW,
      imgH,
      x: -imgW / 2 + tx,
      y: -imgH / 2 + ty,
      scale,
    }
  }, [windowSize, activePart])

  const cameraTransition = prefersReducedMotion
    ? { duration: 0 }
    : { duration: softMotionDuration.handoff, ease: softEditorialEase }

  const scrollToStep = useCallback(
    (index: number) => {
      stepRefs.current[index]?.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'center',
      })
    },
    [prefersReducedMotion],
  )

  return (
    <section className="relative mx-auto max-w-[1440px] px-0 lg:px-[140px]">
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-x-24">
        <div className="sticky top-[66px] z-20 bg-white pb-2 pt-2 shadow-[0_16px_32px_-22px_rgba(11,11,15,0.22)] lg:top-[86px] lg:h-[calc(100vh-86px)] lg:bg-transparent lg:py-0 lg:shadow-none">
          <div className="flex h-[38vh] flex-col gap-2.5 px-5 lg:h-full lg:justify-center lg:gap-4 lg:px-0 lg:py-10">
            <div className="relative min-h-0 w-full flex-1">
              <div className="absolute inset-0 overflow-hidden rounded-2xl border border-[#ECECEF] bg-[#F7F7F8] lg:rounded-[20px]">
                <div
                  ref={windowRef}
                  className="relative h-full w-full"
                >
                  {windowSize ? (
                    <motion.div
                      className="absolute left-1/2 top-1/2 bg-white shadow-[0_16px_44px_rgba(11,11,15,0.14)] will-change-transform"
                      style={{ width: camera.imgW, height: camera.imgH }}
                      initial={false}
                      animate={{ x: camera.x, y: camera.y, scale: camera.scale }}
                      transition={cameraTransition}
                    >
                      <Image
                        src={certificateImage}
                        alt="A sample NextClinic medical certificate for a fictitious patient, John Smith, issued by a fictitious doctor, Dr Jane Citizen"
                        fill
                        sizes="1400px"
                        priority
                        className="object-contain"
                      />
                      {/* All animated props stay present with stable units —
                          if any starts as `auto`, framer-motion measures
                          keyframes via window.scrollTo, cancelling in-flight
                          smooth scrolls. */}
                      <motion.div
                        aria-hidden="true"
                        className="pointer-events-none absolute border-[#EF8E3B] shadow-[0_0_0_4000px_rgba(11,11,15,0.22)]"
                        style={{
                          left: '0%',
                          top: '0%',
                          width: '100%',
                          height: '100%',
                          borderWidth: 0,
                          borderRadius: 8,
                        }}
                        initial={false}
                        animate={{
                          opacity: activePart ? 1 : 0,
                          left: `${(activePart?.region.x ?? 0) * 100}%`,
                          top: `${(activePart?.region.y ?? 0) * 100}%`,
                          width: `${(activePart?.region.w ?? 1) * 100}%`,
                          height: `${(activePart?.region.h ?? 1) * 100}%`,
                          borderWidth: activePart ? 1.5 / camera.scale : 0,
                          borderRadius: 8 / camera.scale,
                        }}
                        transition={cameraTransition}
                      />
                      {certificateParts.map((part, index) => (
                        <motion.button
                          key={part.id}
                          type="button"
                          aria-label={`Part ${index + 1}: ${part.title}`}
                          onClick={() => scrollToStep(index + 1)}
                          className="absolute flex h-[22px] w-[22px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[#ECECEF] bg-white/95 text-[11px] font-bold text-[#0B0B0F] shadow-[0_4px_14px_rgba(11,11,15,0.14)] transition-colors duration-[420ms] hover:border-[#EF8E3B] hover:text-[#D97B1F] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2563EB] lg:h-[26px] lg:w-[26px] lg:text-xs"
                          style={{
                            left: `${part.dot.x * 100}%`,
                            top: `${part.dot.y * 100}%`,
                          }}
                          initial={false}
                          animate={{ opacity: activePart ? 0 : 1 }}
                          transition={{ duration: 0.3 }}
                          tabIndex={activePart ? -1 : 0}
                        >
                          {index + 1}
                        </motion.button>
                      ))}
                    </motion.div>
                  ) : (
                    <Image
                      src={certificateImage}
                      alt="A sample NextClinic medical certificate for a fictitious patient, John Smith, issued by a fictitious doctor, Dr Jane Citizen"
                      fill
                      sizes="1400px"
                      priority
                      className="object-contain"
                    />
                  )}
                </div>
              </div>
            </div>
            <p className="text-center text-xs font-medium leading-4 text-[#82828B] lg:text-[13px]">
              {activePart ? (
                <>
                  <span className="font-semibold text-[#0B0B0F]">
                    {String((activeStep as Extract<Step, { type: 'part' }>).index + 1).padStart(2, '0')}
                  </span>
                  {' / '}
                  {String(certificateParts.length).padStart(2, '0')}
                  {' · '}
                  {activePart.title}
                </>
              ) : (
                'Sample medical certificate'
              )}
            </p>
          </div>
        </div>

        <div className="px-5 lg:px-0">
          {steps.map((step, index) => (
            <StoryStep
              key={step.id}
              ref={(node: HTMLDivElement | null) => {
                stepRefs.current[index] = node
              }}
              active={index === activeIndex}
              prefersReducedMotion={prefersReducedMotion ?? false}
            >
              {step.type === 'part' ? (
                <PartStepContent step={step} />
              ) : (
                <OverviewStepContent step={step} />
              )}
            </StoryStep>
          ))}
        </div>
      </div>
    </section>
  )
}

function StoryStep({
  ref,
  active,
  prefersReducedMotion,
  children,
}: {
  ref: (node: HTMLDivElement | null) => void
  active: boolean
  prefersReducedMotion: boolean
  children: ReactNode
}) {
  return (
    <div
      ref={ref}
      data-step
      className="flex min-h-[44vh] items-center py-8 lg:min-h-[62vh] lg:py-12"
    >
      <motion.div
        className="relative w-full"
        initial={false}
        animate={{ opacity: active ? 1 : 0.3 }}
        transition={{
          duration: prefersReducedMotion ? 0 : 0.45,
          ease: softEditorialEase,
        }}
      >
        {children}
      </motion.div>
    </div>
  )
}

function PartStepContent({ step }: { step: Extract<Step, { type: 'part' }> }) {
  const { part, index } = step
  const kind = partKindMeta[part.kind]

  return (
    <div className="relative">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -top-16 right-0 select-none font-jakarta text-[96px] font-extrabold leading-none tracking-[-0.02em] lg:-top-20 lg:-right-2 lg:text-[124px]"
        style={{
          WebkitTextStroke: '1.5px rgba(11,11,15,0.13)',
          color: 'transparent',
        }}
      >
        {String(index + 1).padStart(2, '0')}
      </span>
      <p className="text-xs font-semibold uppercase leading-4 tracking-[0.08em] text-[#82828B]">
        Part {String(index + 1).padStart(2, '0')} — {String(certificateParts.length).padStart(2, '0')}
      </p>
      <h2 className="mt-3 font-jakarta text-[27px] font-bold leading-[33px] tracking-[-0.02em] text-[#0B0B0F] lg:text-[32px] lg:leading-[39px]">
        {part.title}
      </h2>
      <p className="mt-4 text-[16px] leading-[25px] text-[#46464E] lg:text-[17px] lg:leading-[27px]">
        {part.what}
      </p>
      <div className="mt-7 border-t border-[#ECECEF] pt-5">
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className={`h-[7px] w-[7px] shrink-0 rounded-full ${kind.dotClassName}`}
          />
          <span
            className={`text-xs font-semibold uppercase leading-4 tracking-[0.025em] ${kind.labelClassName}`}
          >
            Why it’s there — {kind.label}
          </span>
        </div>
        <p className="mt-2.5 text-[15px] leading-[23px] text-[#82828B]">
          {part.why}
        </p>
        {part.whyLink ? (
          <Link
            href={part.whyLink.href}
            className="mt-3 inline-flex text-[14px] font-semibold leading-[18px] text-[#2563EB] transition-colors duration-[420ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:text-[#1749B5] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2563EB]"
          >
            {part.whyLink.label} →
          </Link>
        ) : null}
      </div>
    </div>
  )
}

function OverviewStepContent({
  step,
}: {
  step: Extract<Step, { type: 'overview' }>
}) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase leading-4 tracking-[0.08em] text-[#D97B1F]">
        {step.label}
      </p>
      <h2 className="mt-3 font-jakarta text-[27px] font-bold leading-[33px] tracking-[-0.02em] text-[#0B0B0F] lg:text-[32px] lg:leading-[39px]">
        {step.title}
      </h2>
      <p className="mt-4 text-[16px] leading-[25px] text-[#46464E] lg:text-[17px] lg:leading-[27px]">
        {step.body}
      </p>
      {step.id === 'outro' ? (
        <a
          href="/static/sample-medical-certificate.pdf"
          download="nextclinic-sample-medical-certificate.pdf"
          className="mt-7 inline-flex items-center justify-center rounded-full border border-[#0B0B0F]/15 px-6 py-3 text-[15px] font-semibold leading-[18px] text-[#0B0B0F] transition-colors duration-[420ms] ease-[cubic-bezier(0.22,0.55,0.28,1)] hover:border-[#0B0B0F] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2563EB]"
        >
          Download the sample PDF
        </a>
      ) : null}
    </div>
  )
}
